import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Edit, Trash2, Users, Search, RefreshCw, Eye, X, ChevronRight, Upload, File, Download } from 'lucide-react';
import api, { uploadRequestConfig } from '../../services/api';
import { MAX_FILE_MB, MAX_FILE_SIZE_BYTES } from '../../config/config';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import {
  displayValue, formatBoolean, formatCurrency, formatProfileDate,
  getUserIdentifier, normalizeUserProfiles, toDateInputValue
} from '../../utils/userProfile';
import {
  PAYROLL_PERCENTAGE_FIELDS, PAYROLL_PERCENTAGE_INPUT_FIELDS,
  derivePayrollValues
} from '../../utils/payrollUtils';
import { ORGANIZATION_OPTIONS } from '../../constants/organizations';
import { viewDocument, downloadDocument } from '../../utils/documentAccess';
import {
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_OTHER,
  DOCUMENT_LABEL_MAX_LENGTH,
  DOCUMENT_LABEL_MIN_LENGTH,
  documentDisplayName,
} from '../../constants/documentTypes';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const EDIT_TABS = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'contact', label: 'Contact' },
  { id: 'documents', label: 'Documents' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'education', label: 'Education' },
  { id: 'experience', label: 'Experience' },
  { id: 'admin', label: 'Settings' },
  { id: 'reportingManager', label: 'Reporting Manager' },
];

const NON_NEGATIVE_NUMBER_FIELDS = [
  'ctcBasic', 'ctcHRA', 'ctcSpecialAllowance',
  'ctcTravelAllowance', 'ctcITAllowance', 'payScale', 'probationDays',
  'internshipDays',
];

const YEAR_FIELDS = [
  'schoolYearOfGraduation', 'universityYearOfGraduation',
  'postGraduationYearOfGraduation', 'highestEducationYearOfPassing',
];

const NUMERIC_FIELDS = [
  ...NON_NEGATIVE_NUMBER_FIELDS, ...YEAR_FIELDS, 'totalExperience',
  'schoolPercentageOrCGPA', 'universityPercentageOrCGPA',
  'postGraduationPercentageOrCGPA', 'highestEducationPercentageOrCGPA',
];

const BOOLEAN_FIELDS = ['physicalChallenged', 'relievingLetterSubmitted', 'isIntern'];
const FORM_DATE_FIELDS = ['dateOfBirth', 'previousStartDate', 'previousEndDate', 'joinDate'];

const getInitialFormData = () => ({
  name: '', email: '', password: '',
  organization: '', employeeId: '',
  joinDate: new Date().toISOString().split('T')[0],
  probationDays: 60,
  isIntern: false,
  internshipDays: 90,
  isActive: true,
  dateOfBirth: '', gender: '', maritalStatus: '',
  jobTitle: '', bloodGroup: '', physicalChallenged: false,
  mobileNumber: '', alternateNumber: '', emergencyContactNumber: '',
  emergencyName: '',
  emergencyRelation: '',
  presentAddress: '', permanentAddress: '',
  city: '', state: '', pinCode: '',
  panNumber: '', aadhaarNumber: '', passportNumber: '',
  pfDetails: '', medicalInsurance: '',
  ctcBasic: '',
  ctcHRAPercentage: '', ctcHRA: '',
  ctcSpecialAllowancePercentage: '', ctcSpecialAllowance: '',
  ctcTravelAllowancePercentage: '', ctcTravelAllowance: '',
  ctcITAllowancePercentage: '', ctcITAllowance: '',
  payScale: '',
  schoolName: '', schoolBoard: '', schoolYearOfGraduation: '', schoolPercentageOrCGPA: '',
  universityName: '', universityBoard: '', universityYearOfGraduation: '', universityPercentageOrCGPA: '',
  postGraduationName: '', postGraduationBoard: '', postGraduationYearOfGraduation: '', postGraduationPercentageOrCGPA: '',
  highestEducationCategory: '', highestEducationInstitutionName: '', highestEducationBoard: '',
  highestEducationYearOfPassing: '', highestEducationPercentageOrCGPA: '',
  totalExperience: '',
  previousCompanyName: '', previousCompanyLocation: '', previousJobTitle: '',
  previousSupervisorName: '', previousSupervisorContact: '',
  previousStartDate: '', previousEndDate: '',
  reasonForLeaving: '', relievingLetterSubmitted: false,
  reference1Name: '', reference1Address: '', reference1Mobile: '', reference1Relationship: '',
  reference2Name: '', reference2Address: '', reference2Mobile: '', reference2Relationship: '',
  annual_earned_leave: 12, annual_sick_leave: 7,
  reportingManagerEmployeeId: '',
});

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors';
const disabledInputCls = `${inputCls} bg-gray-50 cursor-not-allowed text-gray-500`;

const compareEmployeeIds = (leftId, rightId) => {
  const left = (leftId || '').trim();
  const right = (rightId || '').trim();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
};

const EmployeeManagement = () => {
  const location = useLocation();
  const probationPath = location.pathname.startsWith('/superadmin') ? '/superadmin/probation' : '/admin/probation';
  const internshipPath = location.pathname.startsWith('/superadmin') ? '/superadmin/internship' : '/admin/internship';
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  // Read-only display only, for a branch-scheme employee's fixed leave balances -
  // never flows into formData / the save payload.
  const [viewLeaveSettings, setViewLeaveSettings] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [formData, setFormData] = useState(getInitialFormData());
  const [activeTab, setActiveTab] = useState('basic');
  const [submitting, setSubmitting] = useState(false);

  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [replacing, setReplacing] = useState({});
  const [selectedDocType, setSelectedDocType] = useState('');
  const [customDocumentLabel, setCustomDocumentLabel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState({});
  const pendingIdRef = useRef(0);
  const [originalPayroll, setOriginalPayroll] = useState(null);
  const employeesFetchSeq = useRef(0);

  /** Latest active `offer_letter` document for the view-details modal (from onboarding or Documents tab). */
  const [viewModalOfferLetterDoc, setViewModalOfferLetterDoc] = useState(null);
  const [loadingViewModalOfferLetter, setLoadingViewModalOfferLetter] = useState(false);
  const [openingOfferLetter, setOpeningOfferLetter] = useState(false);

  const fetchEmployees = useCallback(async () => {
    const seq = ++employeesFetchSeq.current;
    try {
      setLoading(true);
      const response = await api.get('/admin/employees', {
        params: { includeInactive: true },
      });
      if (seq !== employeesFetchSeq.current) return;
      setEmployees(normalizeUserProfiles(response.data.employees || []));
    } catch (error) {
      if (seq !== employeesFetchSeq.current) return;
      console.error('Error fetching employees:', error);
      toast.error('Failed to fetch employees');
    } finally {
      if (seq === employeesFetchSeq.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    if (!viewingEmployee) {
      setViewModalOfferLetterDoc(null);
      return;
    }
    const empId = getUserIdentifier(viewingEmployee);
    if (!empId) {
      setViewModalOfferLetterDoc(null);
      return;
    }
    let cancelled = false;
    setLoadingViewModalOfferLetter(true);
    api
      .get(`/documents/employee/${empId}`)
      .then((res) => {
        if (cancelled) return;
        const docs = res.data?.documents || [];
        const offer = docs.find(
          (d) => d && d.documentType === 'offer_letter' && d.isActive !== false,
        );
        setViewModalOfferLetterDoc(offer || null);
      })
      .catch(() => {
        if (!cancelled) setViewModalOfferLetterDoc(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingViewModalOfferLetter(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewingEmployee]);

  const handleViewOfferLetter = async () => {
    if (!viewModalOfferLetterDoc?._id) return;
    setOpeningOfferLetter(true);
    try {
      const res = await api.get(`/documents/${viewModalOfferLetterDoc._id}/access-url`);
      const accessUrl = res.data?.accessUrl;
      if (!accessUrl) {
        toast.error('Could not get document link');
        return;
      }
      window.open(accessUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to open offer letter');
    } finally {
      setOpeningOfferLetter(false);
    }
  };

  const handleDownloadOfferLetter = async () => {
    if (!viewModalOfferLetterDoc?._id) return;
    setOpeningOfferLetter(true);
    try {
      const res = await api.get(`/documents/${viewModalOfferLetterDoc._id}/access-url`);
      const accessUrl = res.data?.accessUrl;
      if (!accessUrl) {
        toast.error('Could not get document link');
        return;
      }
      const name =
        viewModalOfferLetterDoc.originalFileName?.replace(/[^\w.\-()\s]/g, '_') ||
        `offer-letter-${viewModalOfferLetterDoc._id}`;
      try {
        const fileRes = await fetch(accessUrl, { mode: 'cors' });
        if (!fileRes.ok) throw new Error('Download failed');
        const blob = await fileRes.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch {
        window.open(accessUrl, '_blank', 'noopener,noreferrer');
        toast.info('Opened in a new tab — use Save or Print to keep a copy if download did not start.');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to download offer letter');
    } finally {
      setOpeningOfferLetter(false);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };

      if (field === 'ctcBasic' || PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(field)) {
        return { ...next, ...derivePayrollValues(next) };
      }

      const amountMatch = PAYROLL_PERCENTAGE_FIELDS.find(f => f.amountField === field);
      if (amountMatch) {
        const basic = Number(next.ctcBasic) || 0;
        const amount = Number(value) || 0;
        if (basic > 0) {
          next[amountMatch.percentageField] = String(Math.round((amount / basic) * 10000) / 100);
        }
        let total = basic;
        PAYROLL_PERCENTAGE_FIELDS.forEach(({ amountField }) => {
          total += Number(next[amountField]) || 0;
        });
        next.payScale = basic > 0 ? String(Math.round(total * 100) / 100) : '';
        return next;
      }

      return next;
    });
  }, []);

  const handleOrganizationChange = async (organization) => {
    setFormData(prev => ({ ...prev, organization }));
    if (!editingEmployee && organization) {
      try {
        const response = await api.get('/admin/next-employee-id');
        setFormData(prev => ({ ...prev, organization, employeeId: response.data.employeeId }));
      } catch (error) {
        console.error('Error fetching next employee ID:', error);
        toast.error('Failed to generate employee ID');
      }
    }
  };

  const fetchDocuments = useCallback(async (employeeId) => {
    try {
      setLoadingDocs(true);
      const res = await api.get(`/documents/employee/${employeeId}`);
      setUploadedDocs(res.data.documents || []);
    } catch {
      setUploadedDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  const handleDocFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size must be under ${MAX_FILE_MB} MB`);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleAddDocument = () => {
    if (!selectedDocType) { toast.error('Select a document type'); return; }
    if (selectedDocType === DOCUMENT_TYPE_OTHER) {
      const t = customDocumentLabel.trim();
      if (t.length < DOCUMENT_LABEL_MIN_LENGTH) {
        toast.error(`Enter a document name (${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} characters)`);
        return;
      }
      if (t.length > DOCUMENT_LABEL_MAX_LENGTH) {
        toast.error(`Document name must be at most ${DOCUMENT_LABEL_MAX_LENGTH} characters`);
        return;
      }
    }
    if (!selectedFile) { toast.error('Select a file'); return; }
    const trimmedCustom = customDocumentLabel.trim();
    pendingIdRef.current += 1;
    setPendingDocs(prev => [...prev, {
      id: pendingIdRef.current,
      docType: selectedDocType,
      docTypeLabel:
        selectedDocType === DOCUMENT_TYPE_OTHER
          ? trimmedCustom
          : DOCUMENT_TYPE_OPTIONS.find(d => d.value === selectedDocType)?.label || selectedDocType,
      documentLabel: selectedDocType === DOCUMENT_TYPE_OTHER ? trimmedCustom : '',
      file: selectedFile,
    }]);
    setSelectedDocType('');
    setCustomDocumentLabel('');
    setSelectedFile(null);
    const fi = document.getElementById('emp-doc-file-input');
    if (fi) fi.value = '';
  };

  const handleRemovePending = (id) => setPendingDocs(prev => prev.filter(d => d.id !== id));

  const handleSaveDocuments = async () => {
    if (pendingDocs.length === 0) { toast.error('Add documents first'); return; }
    const employeeId = getUserIdentifier(editingEmployee);
    if (!employeeId) return;
    setUploading(true);
    let success = 0;
    const failedIds = [];
    for (const doc of pendingDocs) {
      try {
        const fd = new FormData();
        fd.append('documentType', doc.docType);
        if (doc.docType === DOCUMENT_TYPE_OTHER && doc.documentLabel) {
          fd.append('documentLabel', doc.documentLabel);
        }
        fd.append('file', doc.file);
        const res = await api.post(`/documents/employee/${employeeId}/upload`, fd, uploadRequestConfig());
        setUploadedDocs(prev => [res.data.document, ...prev]);
        success++;
      } catch (error) {
        console.error(`Upload failed for ${doc.docTypeLabel}:`, error);
        failedIds.push(doc.id);
      }
    }
    if (success > 0) toast.success(`${success} document(s) uploaded`);
    if (failedIds.length > 0) toast.error(`${failedIds.length} document(s) failed`);
    setPendingDocs(prev => prev.filter(d => failedIds.includes(d.id)));
    setUploading(false);
  };

  const handleDeleteDoc = async (docId, titleForConfirm) => {
    if (!window.confirm(`Delete “${titleForConfirm}” document?`)) return;
    setDeleting(prev => ({ ...prev, [docId]: true }));
    try {
      await api.delete(`/documents/${docId}`);
      setUploadedDocs(prev => prev.filter(d => d._id !== docId));
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document');
    } finally {
      setDeleting(prev => ({ ...prev, [docId]: false }));
    }
  };

  // Upload the new file first and only then remove the old one, so a failed upload never loses a document
  const handleReplaceDoc = async (doc, file) => {
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size must be under ${MAX_FILE_MB} MB`);
      return;
    }
    const employeeId = getUserIdentifier(editingEmployee);
    if (!employeeId) return;

    setReplacing(prev => ({ ...prev, [doc._id]: true }));
    try {
      const fd = new FormData();
      fd.append('documentType', doc.documentType);
      if (doc.documentType === DOCUMENT_TYPE_OTHER && doc.documentLabel) {
        fd.append('documentLabel', doc.documentLabel);
      }
      fd.append('file', file);
      const res = await api.post(`/documents/employee/${employeeId}/upload`, fd, uploadRequestConfig());

      try {
        await api.delete(`/documents/${doc._id}`);
      } catch {
        toast.warning('New file saved, but the old copy could not be removed');
      }

      setUploadedDocs(prev => prev.map(d => (d._id === doc._id ? res.data.document : d)));
      toast.success(`${documentDisplayName(doc, DOCUMENT_TYPE_OPTIONS)} replaced`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to replace document');
    } finally {
      setReplacing(prev => ({ ...prev, [doc._id]: false }));
    }
  };

  const handleEdit = async (employee) => {
    setEditingEmployee(employee);
    const employeeKey = getUserIdentifier(employee);
    if (!employeeKey) { toast.error('Unable to identify employee'); return; }

    setPendingDocs([]);
    setSelectedDocType('');
    setCustomDocumentLabel('');
    setSelectedFile(null);
    const fi = document.getElementById('emp-doc-file-input');
    if (fi) fi.value = '';

    let leaveSettings = { annual_earned_leave: 12, annual_sick_leave: 7 };
    try {
      const res = await api.get(`/admin/employee/${employeeKey}/leave-settings`);
      leaveSettings = res.data.leaveSettings || leaveSettings;
    } catch { /* defaults */ }
    setViewLeaveSettings(leaveSettings);

    const initial = getInitialFormData();
    const populated = { ...initial };

    Object.keys(initial).forEach(key => {
      if (['annual_earned_leave', 'annual_sick_leave'].includes(key)) {
        populated[key] = leaveSettings[key] ?? initial[key];
        return;
      }
      if (key === 'password') return;
      if (key === 'reportingManagerEmployeeId') { populated[key] = employee.reportingManager?.employeeId || ''; return; }
      if (key === 'isActive') { populated[key] = employee.isActive ?? true; return; }
      // Employees created before the internship feature have no stored value here.
      if (key === 'internshipDays') { populated[key] = employee.internshipDays ?? 90; return; }
      if (BOOLEAN_FIELDS.includes(key)) { populated[key] = Boolean(employee[key]); return; }
      if (FORM_DATE_FIELDS.includes(key)) {
        populated[key] = toDateInputValue(employee[key]) || (key === 'joinDate' ? initial.joinDate : '');
        return;
      }
      // Treat legacy 0/invalid years as empty, so Save doesn't fail validation.
      if (YEAR_FIELDS.includes(key)) {
        const raw = employee[key];
        const n = raw === null || raw === undefined || raw === '' ? null : Number(raw);
        populated[key] = !n || !Number.isFinite(n) ? '' : String(n);
        return;
      }
      if (PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(key)) {
        const basic = Number(employee.ctcBasic) || 0;
        const pField = PAYROLL_PERCENTAGE_FIELDS.find(f => f.percentageField === key);
        if (pField && basic > 0) {
          const amount = Number(employee[pField.amountField]) || 0;
          populated[key] = amount ? String(Math.round((amount / basic) * 10000) / 100) : '';
        }
        return;
      }
      const val = employee[key];
      populated[key] = val === null || val === undefined ? '' : String(val);
    });

    setFormData(populated);
    setOriginalPayroll({
      ctcBasic: employee.ctcBasic, ctcHRA: employee.ctcHRA,
      ctcSpecialAllowance: employee.ctcSpecialAllowance,
      ctcTravelAllowance: employee.ctcTravelAllowance,
      ctcITAllowance: employee.ctcITAllowance, payScale: employee.payScale,
    });
    fetchDocuments(employeeKey);
    setActiveTab('basic');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) { toast.error('Name is required'); return; }
    if (!formData.email?.trim()) { toast.error('Email is required'); return; }
    if (!editingEmployee && !formData.password?.trim()) { toast.error('Password is required'); return; }
    if (!formData.employeeId?.trim()) { toast.error('Employee ID is required'); return; }
    if (!formData.organization?.trim()) { toast.error('Organization is required'); return; }
    if (!formData.jobTitle?.trim()) { toast.error('Job Title is required'); return; }
    if (!formData.joinDate) { toast.error('Join Date is required'); return; }

    for (const f of YEAR_FIELDS) {
      const y = formData[f];
      if (y !== '' && y !== null && y !== undefined) {
        const n = Number(y);
        // treat 0/NaN as "not provided" (legacy values)
        if (!n || !Number.isFinite(n)) continue;
        if (n < 1900 || n > 2100) {
        toast.error('Year must be between 1900 and 2100'); return;
      }
      }
    }
    for (const f of NON_NEGATIVE_NUMBER_FIELDS) {
      if (formData[f] !== '' && Number(formData[f]) < 0) {
        toast.error('Value cannot be negative'); return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(key)) return;
        if (['annual_earned_leave', 'annual_sick_leave'].includes(key)) return;

        if (key === 'password') {
          if (value && value.trim()) payload[key] = value;
          return;
        }
        if (BOOLEAN_FIELDS.includes(key) || key === 'isActive') {
          payload[key] = Boolean(value);
          return;
        }
        if (FORM_DATE_FIELDS.includes(key)) {
          payload[key] = value || null;
          return;
        }
        if (key === 'internshipDays') {
          payload[key] = value === '' || value === null || value === undefined ? 90 : Number(value);
          return;
        }
        if (NUMERIC_FIELDS.includes(key)) {
          payload[key] = value === '' ? null : Number(value);
          return;
        }
        const trimmed = typeof value === 'string' ? value.trim() : value;
        payload[key] = trimmed === '' ? null : trimmed;
      });

      if (!editingEmployee) delete payload.isActive;

      if (editingEmployee) {
        const employeeKey = getUserIdentifier(editingEmployee);
        if (!employeeKey) { toast.error('Unable to identify employee'); return; }
        if (!payload.password) delete payload.password;

        // Files added under "Upload Documents" are saved too, not only via "Upload now"
        if (pendingDocs.length > 0) await handleSaveDocuments();

        await api.put(`/admin/employee/${employeeKey}`, payload);
        // Branch-scheme employees have fixed, non-editable monthly quotas - the
        // backend rejects this call for them, so skip it client-side too.
        if (!editingEmployee.branch) {
          await api.put(`/admin/employee/${employeeKey}/leave-settings`, {
            annual_earned_leave: parseFloat(formData.annual_earned_leave),
            annual_sick_leave: parseFloat(formData.annual_sick_leave),
          });
        }
        toast.success('Employee updated successfully!');
      } else {
        await api.post('/admin/employee', payload);
        toast.success('Employee created — pending Super Admin approval');
      }

      setShowModal(false);
      setEditingEmployee(null);
      setViewLeaveSettings(null);
      setFormData(getInitialFormData());
      fetchEmployees();
    } catch (error) {
      console.error('Employee save error:', error);
      const errData = error.response?.data;
      toast.error(errData?.errors?.[0]?.msg || errData?.message || 'Failed to save employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
    try {
      const employeeKey = getUserIdentifier(employee);
      if (!employeeKey) { toast.error('Unable to identify employee'); return; }
      await api.put(`/admin/employee/${employeeKey}`, { isActive: false });
      toast.success('Employee deactivated successfully!');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to deactivate employee');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEmployee(null);
    setViewLeaveSettings(null);
    setFormData(getInitialFormData());
    setActiveTab('basic');
    setUploadedDocs([]);
    setPendingDocs([]);
    setSelectedDocType('');
    setCustomDocumentLabel('');
    setSelectedFile(null);
    setOriginalPayroll(null);
  };

  const filteredEmployees = employees
    .filter(employee => {
      const matchesSearch = (employee.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (employee.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (employee.employeeId && employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesOrganization =
        organizationFilter === 'all' ||
        (employee.organization || '').toLowerCase() === organizationFilter.toLowerCase();
      return matchesSearch && matchesOrganization;
    })
    .sort((a, b) => compareEmployeeIds(a.employeeId, b.employeeId));

  // ── Tab content renderers ────────────────────────────────────

  const renderBasicTab = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
          <input type="text" value={formData.name} onChange={e => handleFieldChange('name', e.target.value)} className={inputCls} placeholder="Enter full name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
          <input type="date" value={formData.dateOfBirth} onChange={e => handleFieldChange('dateOfBirth', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
          <select value={formData.gender} onChange={e => handleFieldChange('gender', e.target.value)} className={inputCls}>
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
          <select value={formData.maritalStatus} onChange={e => handleFieldChange('maritalStatus', e.target.value)} className={inputCls}>
            <option value="">Select Status</option>
            <option value="single">Single</option>
            <option value="married">Married</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Job Title <span className="text-red-500">*</span></label>
          <input type="text" value={formData.jobTitle} onChange={e => handleFieldChange('jobTitle', e.target.value)} className={inputCls} placeholder="e.g., Software Engineer" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
          <input type="text" value={formData.bloodGroup} onChange={e => handleFieldChange('bloodGroup', e.target.value)} className={inputCls} placeholder="e.g., A+" />
        </div>
        <div className="flex items-center gap-3 pt-5">
          <input type="checkbox" id="edit-physicalChallenged" checked={formData.physicalChallenged} onChange={e => handleFieldChange('physicalChallenged', e.target.checked)} className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
          <label htmlFor="edit-physicalChallenged" className="text-sm font-medium text-gray-700">Physically Challenged</label>
        </div>
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
          <input type="email" value={formData.email} onChange={e => handleFieldChange('email', e.target.value)} className={inputCls} placeholder="name@company.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
          <input type="text" value={formData.mobileNumber} onChange={e => handleFieldChange('mobileNumber', e.target.value)} className={inputCls} placeholder="10-digit mobile" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Number</label>
          <input type="text" value={formData.alternateNumber} onChange={e => handleFieldChange('alternateNumber', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Name <span className="text-red-500">*</span></label>
          <input type="text" value={formData.emergencyName} onChange={e => handleFieldChange('emergencyName', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Relation <span className="text-red-500">*</span></label>
          <input type="text" value={formData.emergencyRelation} onChange={e => handleFieldChange('emergencyRelation', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
          <input type="text" value={formData.emergencyContactNumber} onChange={e => handleFieldChange('emergencyContactNumber', e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="border-t border-gray-100 pt-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Address</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Present Address</label>
            <textarea rows={2} value={formData.presentAddress} onChange={e => handleFieldChange('presentAddress', e.target.value)} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Permanent Address</label>
            <textarea rows={2} value={formData.permanentAddress} onChange={e => handleFieldChange('permanentAddress', e.target.value)} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input type="text" value={formData.city} onChange={e => handleFieldChange('city', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input type="text" value={formData.state} onChange={e => handleFieldChange('state', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pin Code</label>
            <input type="text" value={formData.pinCode} onChange={e => handleFieldChange('pinCode', e.target.value)} className={inputCls} placeholder="6-digit" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocumentsTab = () => {
    const employeeId = editingEmployee ? getUserIdentifier(editingEmployee) : null;
    return (
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Document Numbers</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
              <input type="text" value={formData.panNumber} onChange={e => handleFieldChange('panNumber', e.target.value.toUpperCase())} className={inputCls} placeholder="ABCDE1234F" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Number</label>
              <input type="text" value={formData.aadhaarNumber} onChange={e => handleFieldChange('aadhaarNumber', e.target.value)} className={inputCls} placeholder="12-digit Aadhaar" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passport Number</label>
              <input type="text" value={formData.passportNumber} onChange={e => handleFieldChange('passportNumber', e.target.value.toUpperCase())} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PF Details</label>
              <input type="text" value={formData.pfDetails} onChange={e => handleFieldChange('pfDetails', e.target.value)} className={inputCls} placeholder="PF account number" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Medical Insurance</label>
              <input type="text" value={formData.medicalInsurance} onChange={e => handleFieldChange('medicalInsurance', e.target.value)} className={inputCls} placeholder="Policy number or details" />
            </div>
          </div>
        </div>
        { editingEmployee && employeeId && (
          <div className="border-t border-gray-100 pt-5 space-y-4">
            <h4 className="text-sm font-semibold text-gray-800">Upload Documents</h4>

            <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
                  <select
                    value={selectedDocType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedDocType(v);
                      if (v !== DOCUMENT_TYPE_OTHER) setCustomDocumentLabel('');
                    }}
                    className={inputCls}
                  >
                    <option value="">Select type</option>
                    {DOCUMENT_TYPE_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">File</label>
                  <label htmlFor="emp-doc-file-input" className="flex items-center gap-2 w-full cursor-pointer rounded-lg border border-dashed border-gray-400 bg-white px-3 py-2 text-sm text-gray-500 hover:border-primary-500 hover:bg-primary-50 transition-colors">
                    <Upload className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{selectedFile ? selectedFile.name : 'Browse file...'}</span>
                  </label>
                  <input id="emp-doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocFileChange} className="sr-only" />
                  <p className="mt-0.5 text-xs text-gray-400">PDF, JPG, PNG &middot; max {MAX_FILE_MB} MB</p>
                </div>
                <button
                  type="button"
                  onClick={handleAddDocument}
                  disabled={
                    !selectedDocType
                    || !selectedFile
                    || (selectedDocType === DOCUMENT_TYPE_OTHER
                      && customDocumentLabel.trim().length < DOCUMENT_LABEL_MIN_LENGTH)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + Add
                </button>
              </div>
              {selectedDocType === DOCUMENT_TYPE_OTHER && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Document name</label>
                  <input
                    type="text"
                    value={customDocumentLabel}
                    onChange={(e) => setCustomDocumentLabel(e.target.value)}
                    placeholder={`${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} characters`}
                    maxLength={DOCUMENT_LABEL_MAX_LENGTH}
                    className={inputCls}
                  />
                </div>
              )}
            </div>

            {pendingDocs.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h5 className="text-xs font-semibold text-gray-600">Pending Uploads ({pendingDocs.length})</h5>
                  <button type="button" onClick={handleSaveDocuments} disabled={uploading} className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-1.5">
                    {uploading ? <LoadingSpinner size="sm" /> : <><Upload className="h-3 w-3" /> Upload now</>}
                  </button>
                </div>
                <p className="text-xs text-gray-400">These are also uploaded when you click Save Changes.</p>
                {pendingDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <File className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.docTypeLabel}</p>
                        <p className="text-xs text-gray-500 truncate">{doc.file.name} ({formatFileSize(doc.file.size)})</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => handleRemovePending(doc.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-gray-600">Uploaded Documents</h5>
              {loadingDocs ? (
                <LoadingSpinner size="sm" className="py-4" />
              ) : uploadedDocs.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
                  <File className="mx-auto h-8 w-8 text-gray-300 mb-1" />
                  <p className="text-xs text-gray-400">No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {uploadedDocs.map(doc => (
                    <div key={doc._id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <File className="h-4 w-4 text-primary-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{documentDisplayName(doc, DOCUMENT_TYPE_OPTIONS)}</p>
                          <p className="text-xs text-gray-500 truncate">{doc.originalFileName}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button type="button" onClick={() => viewDocument(doc._id)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors" title="View">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => downloadDocument(doc._id)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors" title="Download">
                          <Download className="h-4 w-4" />
                        </button>
                        <label
                          className={`p-1.5 transition-colors ${replacing[doc._id] ? 'text-gray-300 cursor-wait' : 'text-gray-400 hover:text-amber-600 cursor-pointer'}`}
                          title="Replace"
                        >
                          {replacing[doc._id] ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="sr-only"
                            disabled={replacing[doc._id]}
                            onChange={(e) => {
                              handleReplaceDoc(doc, e.target.files?.[0]);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <button type="button" onClick={() => handleDeleteDoc(doc._id, documentDisplayName(doc, DOCUMENT_TYPE_OPTIONS))} disabled={deleting[doc._id]} className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPayrollTab = () => {
    const hasOriginal = editingEmployee && originalPayroll && Number(originalPayroll.payScale) > 0;

    const ChangeTag = ({ field }) => {
      if (!hasOriginal) return null;
      const cur = Number(originalPayroll[field]) || 0;
      const nxt = Number(formData[field]) || 0;
      if (cur === nxt || (cur === 0 && nxt === 0)) return null;
      const diff = nxt - cur;
      const up = diff > 0;
      const pct = cur > 0 ? Math.round(Math.abs(diff / cur) * 100) : null;
      return (
        <span className={`ml-2 inline-flex items-center gap-0.5 text-xs font-medium ${up ? 'text-green-600' : 'text-red-600'}`}>
          {up ? '+' : '-'}{formatCurrency(Math.abs(diff)).replace('INR ', '')}
          {pct !== null && <span className="opacity-70">({up ? '+' : '-'}{pct}%)</span>}
        </span>
      );
    };

    return (
      <div className="space-y-5">
        {hasOriginal && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
            <h4 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-2">Current monthly CTC</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-blue-900">
              <span>Basic: <strong>{formatCurrency(originalPayroll.ctcBasic)}</strong></span>
              <span>HRA: <strong>{formatCurrency(originalPayroll.ctcHRA)}</strong></span>
              <span>Spl. Allow: <strong>{formatCurrency(originalPayroll.ctcSpecialAllowance)}</strong></span>
              <span>Travel: <strong>{formatCurrency(originalPayroll.ctcTravelAllowance)}</strong></span>
              <span>IT Allow: <strong>{formatCurrency(originalPayroll.ctcITAllowance)}</strong></span>
              <span className="font-bold">Total / month: <strong>{formatCurrency(originalPayroll.payScale)}</strong></span>
            </div>
          </div>
        )}

        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          All amounts are monthly (₹ / month). Do not enter annual CTC — e.g. for ₹40,000/month enter 40000.
        </p>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-sm font-medium text-gray-700">Basic salary (₹ / month)</label>
            <ChangeTag field="ctcBasic" />
          </div>
          <input type="number" min="0" value={formData.ctcBasic} onChange={e => handleFieldChange('ctcBasic', e.target.value)} className={inputCls} placeholder="e.g. 20000" />
          {hasOriginal && <p className="text-xs text-gray-400 mt-0.5">Changing basic recalculates all allowance amounts via their percentages</p>}
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-4">
          <h4 className="text-sm font-semibold text-gray-800">Allowances (monthly)</h4>
          <p className="text-xs text-gray-400 -mt-2">Edit % of monthly basic to auto-calculate monthly amount, or edit amount directly to back-calculate %</p>
          {PAYROLL_PERCENTAGE_FIELDS.map(({ percentageField, amountField, label }) => (
            <div key={percentageField} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{label} %</label>
                <input type="number" min="0" step="0.01" value={formData[percentageField]} onChange={e => handleFieldChange(percentageField, e.target.value)} className={inputCls} placeholder="%" />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-xs font-medium text-gray-600">{label} (₹ / month)</label>
                  <ChangeTag field={amountField} />
                </div>
                <input type="number" min="0" step="0.01" value={formData[amountField]} onChange={e => handleFieldChange(amountField, e.target.value)} className={inputCls} />
              </div>
              <div className="hidden md:block" />
            </div>
          ))}
        </div>

        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-sm font-medium text-gray-700">Total CTC (₹ / month)</label>
            <ChangeTag field="payScale" />
          </div>
          <input type="text" value={formData.payScale} readOnly className={disabledInputCls} />
          <p className="text-xs text-gray-400 mt-0.5">Auto-calculated: monthly basic + all monthly allowances</p>
        </div>
      </div>
    );
  };

  const renderEducationSection = (prefix, title) => {
    const nameField = `${prefix}Name`;
    const boardField = `${prefix}Board`;
    const yearField = prefix === 'highestEducation' ? 'highestEducationYearOfPassing' : `${prefix}YearOfGraduation`;
    const percentField = `${prefix}PercentageOrCGPA`;
    const categoryField = prefix === 'highestEducation' ? 'highestEducationCategory' : null;
    const instField = prefix === 'highestEducation' ? 'highestEducationInstitutionName' : null;

    return (
      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">{title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {categoryField && (
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <input type="text" value={formData[categoryField]} onChange={e => handleFieldChange(categoryField, e.target.value)} className={inputCls} placeholder="e.g., PhD, MBA" />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{instField ? 'Institution Name' : 'School / University'}</label>
            <input type="text" value={formData[instField || nameField]} onChange={e => handleFieldChange(instField || nameField, e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Board / University</label>
            <input type="text" value={formData[boardField]} onChange={e => handleFieldChange(boardField, e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
            <input type="number" min="1900" max="2100" value={formData[yearField]} onChange={e => handleFieldChange(yearField, e.target.value)} className={inputCls} placeholder="YYYY" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">% / CGPA</label>
            <input type="number" step="0.01" min="0" value={formData[percentField]} onChange={e => handleFieldChange(percentField, e.target.value)} className={inputCls} />
          </div>
        </div>
      </div>
    );
  };

  const renderEducationTab = () => (
    <div className="space-y-4">
      {renderEducationSection('school', '10th / School')}
      {renderEducationSection('university', '12th / University')}
      {renderEducationSection('postGraduation', 'Post Graduation')}
      {renderEducationSection('highestEducation', 'Highest Education')}
    </div>
  );

  const renderExperienceTab = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Total Experience (Years)</label>
        <input type="number" min="0" step="0.5" value={formData.totalExperience} onChange={e => handleFieldChange('totalExperience', e.target.value)} className={inputCls} placeholder="e.g., 3.5" />
      </div>

      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Previous Employment</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
            <input type="text" value={formData.previousCompanyName} onChange={e => handleFieldChange('previousCompanyName', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
            <input type="text" value={formData.previousCompanyLocation} onChange={e => handleFieldChange('previousCompanyLocation', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Job Title</label>
            <input type="text" value={formData.previousJobTitle} onChange={e => handleFieldChange('previousJobTitle', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Supervisor Name</label>
            <input type="text" value={formData.previousSupervisorName} onChange={e => handleFieldChange('previousSupervisorName', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Supervisor Contact</label>
            <input type="text" value={formData.previousSupervisorContact} onChange={e => handleFieldChange('previousSupervisorContact', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason for Leaving</label>
            <input type="text" value={formData.reasonForLeaving} onChange={e => handleFieldChange('reasonForLeaving', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
            <input type="date" value={formData.previousStartDate} onChange={e => handleFieldChange('previousStartDate', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
            <input type="date" value={formData.previousEndDate} onChange={e => handleFieldChange('previousEndDate', e.target.value)} className={inputCls} />
          </div>
          <div className="flex items-center gap-3 pt-3">
            <input type="checkbox" id="edit-relievingLetter" checked={formData.relievingLetterSubmitted} onChange={e => handleFieldChange('relievingLetterSubmitted', e.target.checked)} className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
            <label htmlFor="edit-relievingLetter" className="text-xs font-medium text-gray-700">Relieving Letter Submitted</label>
          </div>
        </div>
      </div>

      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">References</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {[1, 2].map(n => (
            <div key={n} className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference {n}</p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input type="text" value={formData[`reference${n}Name`]} onChange={e => handleFieldChange(`reference${n}Name`, e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                <input type="text" value={formData[`reference${n}Address`]} onChange={e => handleFieldChange(`reference${n}Address`, e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
                <input type="text" value={formData[`reference${n}Mobile`]} onChange={e => handleFieldChange(`reference${n}Mobile`, e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Relationship</label>
                <input type="text" value={formData[`reference${n}Relationship`]} onChange={e => handleFieldChange(`reference${n}Relationship`, e.target.value)} className={inputCls} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAdminTab = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Organization <span className="text-red-500">*</span></label>
          <select value={formData.organization} onChange={e => handleOrganizationChange(e.target.value)} disabled={!!editingEmployee} className={editingEmployee ? disabledInputCls : inputCls}>
            <option value="">Select Organization</option>
            {ORGANIZATION_OPTIONS.map(org => <option key={org} value={org}>{org}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID <span className="text-red-500">*</span></label>
          <input type="text" value={formData.employeeId} readOnly className={disabledInputCls} placeholder="Auto-generated" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Join Date <span className="text-red-500">*</span></label>
          <input type="date" value={formData.joinDate} onChange={e => handleFieldChange('joinDate', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password {editingEmployee && <span className="text-xs text-gray-400 font-normal">(blank = keep current)</span>}
          </label>
          <input type="password" value={formData.password} onChange={e => handleFieldChange('password', e.target.value)} className={inputCls} placeholder={editingEmployee ? '••••••••' : 'Min 6 characters'} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Probation Days</label>
          <input type="number" step="1" min="0" value={formData.probationDays} onChange={e => handleFieldChange('probationDays', e.target.value)} className={inputCls} />
          {editingEmployee?.probationEndDate && editingEmployee?.probationStatus === 'probation' && (
            <p className="mt-1 text-xs text-gray-400">Probation ends {formatProfileDate(editingEmployee.probationEndDate)}</p>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Internship</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="inline-flex items-center cursor-pointer gap-3">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.isIntern}
                  onChange={e => handleFieldChange('isIntern', e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${formData.isIntern ? 'bg-indigo-500' : 'bg-gray-300'}`} />
                <div className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.isIntern ? 'translate-x-5' : ''}`} />
              </div>
              <span className={`text-sm font-medium ${formData.isIntern ? 'text-indigo-600' : 'text-gray-500'}`}>
                {formData.isIntern ? 'Intern' : 'Not an intern'}
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-400">
              Interns are exempt from the ₹200 professional tax until HR confirms them as full-time.
            </p>
          </div>
          {formData.isIntern && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Internship Days</label>
              <input type="number" step="1" min="0" value={formData.internshipDays} onChange={e => handleFieldChange('internshipDays', e.target.value)} className={inputCls} />
              {editingEmployee?.internshipEndDate && (
                <p className="mt-1 text-xs text-gray-400">Internship ends {formatProfileDate(editingEmployee.internshipEndDate)}</p>
              )}
            </div>
          )}
        </div>
        {editingEmployee?.internshipStatus && (
          <div className="mt-4 flex items-center gap-3">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
              editingEmployee.internshipStatus === 'confirmed' ? 'bg-green-100 text-green-700'
                : editingEmployee.internshipStatus === 'rejected' ? 'bg-red-100 text-red-700'
                : 'bg-indigo-100 text-indigo-700'
            }`}>
              {editingEmployee.internshipStatus === 'confirmed' ? 'Internship confirmed'
                : editingEmployee.internshipStatus === 'rejected' ? 'Internship rejected'
                : 'Internship ongoing'}
            </span>
            <Link to={internshipPath} className="text-xs font-medium text-primary-600 hover:underline">
              Manage on Internship Status page
            </Link>
          </div>
        )}
      </div>

      {editingEmployee && (
        <>
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Leave Allocation</h4>
            {editingEmployee.branch ? (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
                <p className="font-semibold mb-1">
                  {editingEmployee.branch === 'SSbranch' ? 'SSbranch' : 'Head Office'} — Fixed Monthly Leave Scheme
                </p>
                <ul className="space-y-0.5 text-xs list-disc list-inside">
                  <li>Sick Leave: 1 day/month, resets monthly (no carry-forward)</li>
                  {editingEmployee.branch === 'SSbranch' && (
                    <li>Casual Leave: 1 day/month after confirmation, resets monthly (no carry-forward)</li>
                  )}
                  <li>Earned Leave: 2 days/month after confirmation, accumulates with no cap</li>
                </ul>
                {viewLeaveSettings && (
                  <p className="mt-2 text-xs text-blue-600">
                    Current balance — Sick: {viewLeaveSettings.sick_leaves_left}
                    {editingEmployee.branch === 'SSbranch' ? `, Casual: ${viewLeaveSettings.casual_leaves_left}` : ''}
                    , Earned: {viewLeaveSettings.earned_leaves_left}
                  </p>
                )}
                <p className="mt-2 text-xs text-blue-500 italic">Quotas are fixed by branch and are not individually editable.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Earned Leave (days/year)</label>
                  <input type="number" step="0.5" min="0" value={formData.annual_earned_leave} onChange={e => handleFieldChange('annual_earned_leave', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sick Leave (days/year)</label>
                  <input type="number" step="0.5" min="0" value={formData.annual_sick_leave} onChange={e => handleFieldChange('annual_sick_leave', e.target.value)} className={inputCls} />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Employee Status</h4>
            <label className="inline-flex items-center cursor-pointer gap-3">
              <div className="relative">
                <input type="checkbox" checked={formData.isActive} onChange={e => handleFieldChange('isActive', e.target.checked)} className="sr-only" />
                <div className={`w-11 h-6 rounded-full transition-colors ${formData.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.isActive ? 'translate-x-5' : ''}`} />
              </div>
              <span className={`text-sm font-medium ${formData.isActive ? 'text-green-600' : 'text-red-500'}`}>
                {formData.isActive ? 'Active' : 'Inactive'}
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-400">Inactive employees cannot log in</p>

            <div className="mt-4 flex items-center gap-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                editingEmployee.probationStatus === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {editingEmployee.probationStatus === 'confirmed' ? 'Confirmed' : 'On Probation'}
              </span>
              <Link to={probationPath} className="text-xs font-medium text-primary-600 hover:underline">
                Manage on Probation Status page
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderReportingManagerTab = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Manager's Employee ID</label>
        <input
          type="text"
          value={formData.reportingManagerEmployeeId}
          onChange={e => handleFieldChange('reportingManagerEmployeeId', e.target.value.toUpperCase())}
          className={inputCls}
          placeholder="e.g. PFS0006 — leave blank for none"
        />
        <p className="mt-1 text-xs text-gray-400">
          Enter the Employee ID of the person this employee reports to. Leave blank to remove their reporting manager.
        </p>
      </div>
      {editingEmployee?.reportingManager && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-600">
          Currently assigned: <span className="font-medium text-gray-900">{editingEmployee.reportingManager.name || 'Unknown'}</span>
          {' '}({editingEmployee.reportingManager.employeeId || 'N/A'})
        </div>
      )}
    </div>
  );

  const tabContent = {
    basic: renderBasicTab,
    contact: renderContactTab,
    documents: renderDocumentsTab,
    payroll: renderPayrollTab,
    education: renderEducationTab,
    experience: renderExperienceTab,
    admin: renderAdminTab,
    reportingManager: renderReportingManagerTab,
  };

  // ── View modal helpers ───────────────────────────────────────

  const ViewRow = ({ label, children }) => (
    <div className="flex justify-between py-1.5 gap-4">
      <dt className="text-gray-500 whitespace-nowrap">{label}</dt>
      <dd className="font-medium text-gray-900 text-right">{children}</dd>
    </div>
  );

  const ViewSection = ({ title, children }) => (
    <div className="pt-3 first:pt-0">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</h4>
      <dl className="divide-y divide-gray-50">{children}</dl>
    </div>
  );

  const hasAnyValue = (obj, fields) => fields.some(f => {
    const v = obj[f];
    return v !== null && v !== undefined && v !== '' && v !== false;
  });

  // ── Main render ──────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600">Manage your employees and their information</p>
          <p className="text-sm text-gray-500 mt-1">Use the employee creation page to add new employees.</p>
        </div>
        <button onClick={fetchEmployees} className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Search employees..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>
          <select value={organizationFilter} onChange={e => setOrganizationFilter(e.target.value)} className={inputCls}>
            <option value="all">All Companies</option>
            {ORGANIZATION_OPTIONS.map((org) => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            Employees <span className="text-sm font-normal text-gray-400">({filteredEmployees.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <LoadingSpinner size="lg" className="py-12" />
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No employees found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/60">
                <tr>
                  {['S.No', 'Employee ID', 'Employee', 'Organization', 'Reporting Manager', 'Email', 'Account', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredEmployees.map((employee, index) => (
                  <tr key={getUserIdentifier(employee) || employee.email} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">{index + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">{employee.employeeId || 'N/A'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary-600">{(employee.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{employee.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{employee.organization || 'N/A'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{employee.reportingManager?.employeeId || 'None'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{employee.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${employee.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewingEmployee(employee)} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="View">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleEdit(employee)} className="p-1.5 rounded-md text-primary-500 hover:text-primary-700 hover:bg-primary-50 transition-colors" title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(employee)} className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Deactivate">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── View Employee Modal ──────────────────────────────── */}
      {viewingEmployee && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center pt-12 pb-8 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl mx-4">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Employee Details</h3>
              <button onClick={() => setViewingEmployee(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="flex items-start gap-4 pb-4 border-b border-gray-100">
                <div className="h-14 w-14 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-primary-600">{(viewingEmployee.name || '?')[0].toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 truncate">{viewingEmployee.name}</p>
                  <p className="text-sm text-gray-500 truncate">{viewingEmployee.email}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400">{viewingEmployee.employeeId || 'No ID'}</span>
                    <span className="text-gray-200">&middot;</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${viewingEmployee.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {viewingEmployee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-col items-end gap-2 pt-0.5">
                  {loadingViewModalOfferLetter ? (
                    <LoadingSpinner size="sm" />
                  ) : viewModalOfferLetterDoc ? (
                    <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={handleViewOfferLetter}
                        disabled={openingOfferLetter}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                        title="Open offer letter in a new tab"
                      >
                        <Eye className="h-4 w-4 text-primary-600" aria-hidden />
                        View offer letter
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadOfferLetter}
                        disabled={openingOfferLetter}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                        title="Download offer letter"
                      >
                        <Download className="h-4 w-4 text-primary-600" aria-hidden />
                        Download
                      </button>
                    </div>
                  ) : (
                    <span className="max-w-[11rem] text-right text-xs text-gray-400" title="Upload during onboarding (Personal step) or Edit → Documents">
                      No offer letter on file
                    </span>
                  )}
                </div>
              </div>

              <ViewSection title="Employment">
                <ViewRow label="Organization">{displayValue(viewingEmployee.organization)}</ViewRow>
                <ViewRow label="Job Title">{displayValue(viewingEmployee.jobTitle)}</ViewRow>
                <ViewRow label="Join Date">{formatProfileDate(viewingEmployee.joinDate)}</ViewRow>
                <ViewRow label="Employment Type">
                  {viewingEmployee.isIntern ? 'Intern' : 'Full-time'}
                </ViewRow>
                {viewingEmployee.isIntern && (
                  <ViewRow label="Internship">
                    {`${viewingEmployee.internshipDays ?? 90} days · ends ${formatProfileDate(viewingEmployee.internshipEndDate)}`}
                  </ViewRow>
                )}
                <ViewRow label="Probation Status">
                  {viewingEmployee.probationStatus === 'confirmed'
                    ? `Confirmed on ${formatProfileDate(viewingEmployee.confirmedAt)}`
                    : `On probation until ${formatProfileDate(viewingEmployee.probationEndDate)}`}
                </ViewRow>
              </ViewSection>

              <ViewSection title="Personal">
                <ViewRow label="Date of Birth">{formatProfileDate(viewingEmployee.dateOfBirth)}</ViewRow>
                <ViewRow label="Gender">{displayValue(viewingEmployee.gender)}</ViewRow>
                <ViewRow label="Marital Status">{displayValue(viewingEmployee.maritalStatus)}</ViewRow>
                <ViewRow label="Blood Group">{displayValue(viewingEmployee.bloodGroup)}</ViewRow>
                <ViewRow label="Physically Challenged">{formatBoolean(viewingEmployee.physicalChallenged)}</ViewRow>
              </ViewSection>

              <ViewSection title="Contact">
                <ViewRow label="Mobile">{displayValue(viewingEmployee.mobileNumber)}</ViewRow>
                <ViewRow label="Alternate">{displayValue(viewingEmployee.alternateNumber)}</ViewRow>
                <ViewRow label="Emergency Name">{displayValue(viewingEmployee.emergencyName)}</ViewRow>
                <ViewRow label="Emergency Relation">{displayValue(viewingEmployee.emergencyRelation)}</ViewRow>
                <ViewRow label="Emergency">{displayValue(viewingEmployee.emergencyContactNumber)}</ViewRow>
              </ViewSection>

              <ViewSection title="Address">
                <ViewRow label="Present Address">{displayValue(viewingEmployee.presentAddress || viewingEmployee.address)}</ViewRow>
                <ViewRow label="Permanent Address">{displayValue(viewingEmployee.permanentAddress)}</ViewRow>
                <ViewRow label="City">{displayValue(viewingEmployee.city)}</ViewRow>
                <ViewRow label="State">{displayValue(viewingEmployee.state)}</ViewRow>
                <ViewRow label="Pin Code">{displayValue(viewingEmployee.pinCode)}</ViewRow>
              </ViewSection>

              <ViewSection title="Documents &amp; Compliance">
                <ViewRow label="PAN Number">{displayValue(viewingEmployee.panNumber)}</ViewRow>
                <ViewRow label="Aadhaar Number">{displayValue(viewingEmployee.aadhaarNumber)}</ViewRow>
                <ViewRow label="Passport">{displayValue(viewingEmployee.passportNumber)}</ViewRow>
                <ViewRow label="PF Details">{displayValue(viewingEmployee.pfDetails)}</ViewRow>
                <ViewRow label="Medical Insurance">{displayValue(viewingEmployee.medicalInsurance)}</ViewRow>
              </ViewSection>

              <ViewSection title="Payroll (monthly)">
                <ViewRow label="Basic salary (₹ / month)">{formatCurrency(viewingEmployee.ctcBasic)}</ViewRow>
                <ViewRow label="HRA (₹ / month)">{formatCurrency(viewingEmployee.ctcHRA)}</ViewRow>
                <ViewRow label="Special allowance (₹ / month)">{formatCurrency(viewingEmployee.ctcSpecialAllowance)}</ViewRow>
                <ViewRow label="Travel allowance (₹ / month)">{formatCurrency(viewingEmployee.ctcTravelAllowance)}</ViewRow>
                <ViewRow label="IT allowance (₹ / month)">{formatCurrency(viewingEmployee.ctcITAllowance)}</ViewRow>
                <ViewRow label="Total CTC (₹ / month)">{formatCurrency(viewingEmployee.payScale)}</ViewRow>
              </ViewSection>

              <ViewSection title="Education">
                <ViewRow label="School">
                  {displayValue(viewingEmployee.schoolName)}
                  {viewingEmployee.schoolYearOfGraduation ? ` (${displayValue(viewingEmployee.schoolYearOfGraduation)})` : ''}
                </ViewRow>
                <ViewRow label="University">
                  {displayValue(viewingEmployee.universityName)}
                  {viewingEmployee.universityYearOfGraduation ? ` (${displayValue(viewingEmployee.universityYearOfGraduation)})` : ''}
                </ViewRow>
                <ViewRow label="Post Graduation">
                  {displayValue(viewingEmployee.postGraduationName)}
                  {viewingEmployee.postGraduationYearOfGraduation ? ` (${displayValue(viewingEmployee.postGraduationYearOfGraduation)})` : ''}
                </ViewRow>
                <ViewRow label="Highest Education">
                  {displayValue(viewingEmployee.highestEducationInstitutionName || viewingEmployee.highestEducationCategory)}
                  {viewingEmployee.highestEducationYearOfPassing ? ` (${displayValue(viewingEmployee.highestEducationYearOfPassing)})` : ''}
                </ViewRow>
              </ViewSection>

              {hasAnyValue(viewingEmployee, ['previousCompanyName', 'totalExperience']) && (
                <ViewSection title="Experience">
                  <ViewRow label="Total Experience">{viewingEmployee.totalExperience != null ? `${viewingEmployee.totalExperience} years` : 'N/A'}</ViewRow>
                  {viewingEmployee.previousCompanyName && (
                    <>
                      <ViewRow label="Previous Company">{viewingEmployee.previousCompanyName}</ViewRow>
                      <ViewRow label="Previous Title">{displayValue(viewingEmployee.previousJobTitle)}</ViewRow>
                      <ViewRow label="Duration">{formatProfileDate(viewingEmployee.previousStartDate)} &ndash; {formatProfileDate(viewingEmployee.previousEndDate)}</ViewRow>
                      <ViewRow label="Reason for Leaving">{displayValue(viewingEmployee.reasonForLeaving)}</ViewRow>
                    </>
                  )}
                </ViewSection>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setViewingEmployee(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Close
              </button>
              <button onClick={() => { setViewingEmployee(null); handleEdit(viewingEmployee); }} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-1.5">
                <Edit className="h-3.5 w-3.5" />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Employee Modal ──────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center pt-6 pb-6 overflow-y-auto">
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-xl mx-4 flex flex-col max-h-[calc(100vh-3rem)]">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingEmployee ? 'Edit Employee' : 'Add Employee'}
                </h3>
                {editingEmployee && (
                  <p className="text-sm text-gray-400 mt-0.5">
                    {editingEmployee.employeeId} &middot; {editingEmployee.organization}
                  </p>
                )}
              </div>
              <button onClick={closeModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 border-b border-gray-100 flex-shrink-0 overflow-x-auto">
              <nav className="flex gap-1 -mb-px" aria-label="Tabs">
                {EDIT_TABS.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {tabContent[activeTab]?.()}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
                <div className="flex gap-2">
                  {activeTab !== EDIT_TABS[0].id && (
                    <button
                      type="button"
                      onClick={() => {
                        const idx = EDIT_TABS.findIndex(t => t.id === activeTab);
                        if (idx > 0) setActiveTab(EDIT_TABS[idx - 1].id);
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Previous
                    </button>
                  )}
                  {activeTab !== EDIT_TABS[EDIT_TABS.length - 1].id && (
                    <button
                      type="button"
                      onClick={() => {
                        const idx = EDIT_TABS.findIndex(t => t.id === activeTab);
                        if (idx < EDIT_TABS.length - 1) setActiveTab(EDIT_TABS[idx + 1].id);
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
                    >
                      Next <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={closeModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className="px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-2">
                    {submitting ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
